import { useTranslation } from "react-i18next";
import { AlertCircle, Trash2 } from "lucide-react";
import clsx from "clsx";
import { Button } from "components/common/Button";
import { InlineSwitcher } from "components/common/InlineSwitcher";
import { ApprovalStatusCheckbox } from "components/SmartSurveyor/ReviewPage/ApprovalStatusCheckbox";
import type { MouseEvent as ReactMouseEvent } from "react";
import type { SelectionAreaMode } from "../utils/types";
import type { AreaRowProps } from "./types";

export const AreaRow = ({
  area,
  index,
  isSelected,
  onSelect,
  onToggleFlag,
  onSetMode,
  onRequestDelete,
}: AreaRowProps) => {
  const { t } = useTranslation();

  const isApplied = area.appliesToElements || area.appliesToRoutes;
  const isIncludeMode = area.mode === "include";

  const handleDeleteClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onRequestDelete(area.id);
  };

  const handleModeChange = (mode: SelectionAreaMode) => {
    onSetMode({ areaId: area.id, mode });
  };

  const stopRowSelection = (event: ReactMouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  return (
    <div
      className={clsx(
        "flex flex-col gap-2 p-3 rounded-md border cursor-pointer",
        !isSelected && "border-gray-200 hover:bg-gray-50",
        isSelected &&
          (isIncludeMode
            ? "border-emerald-400 bg-emerald-50"
            : "border-red-400 bg-red-50"),
      )}
      onClick={() => onSelect(String(area.id))}
    >
      <div className="flex items-center gap-x-3">
        <span className="text-base font-medium text-gray-900">
          {t("smartSurveyor.selectionAreas.areaLabel", { index })}
        </span>
        {!isApplied && (
          <span className="flex items-center gap-1 text-sm text-amber-600">
            <AlertCircle size={14} />
            {t("smartSurveyor.selectionAreas.notApplied")}
          </span>
        )}
      </div>
      <div className="flex items-center gap-x-3">
        <span className="w-32 shrink-0 whitespace-nowrap text-sm text-gray-500">
          {t("smartSurveyor.selectionAreas.selectionMode")}
        </span>
        <div onClick={stopRowSelection}>
          <InlineSwitcher<SelectionAreaMode>
            value={area.mode}
            onChange={handleModeChange}
            listClassName="text-sm"
            // Override the switcher's blue so the active mode matches its polygon color.
            activeItemClassName={clsx(
              "font-semibold",
              isIncludeMode ? "!text-emerald-700" : "!text-red-700",
            )}
            options={[
              {
                value: "exclude",
                label: t("smartSurveyor.selectionAreas.modeExclude"),
              },
              {
                value: "include",
                label: t("smartSurveyor.selectionAreas.modeInclude"),
              },
            ]}
          />
        </div>
      </div>
      <div className="flex items-center gap-x-3">
        <span className="w-32 shrink-0 whitespace-nowrap text-sm text-gray-500">
          {t("smartSurveyor.selectionAreas.appliesTo")}
        </span>
        <label
          className="flex items-center gap-1.5 whitespace-nowrap text-sm text-gray-600 cursor-pointer"
          onClick={stopRowSelection}
        >
          <ApprovalStatusCheckbox
            checked={area.appliesToElements}
            onToggle={() =>
              onToggleFlag({ areaId: area.id, flag: "appliesToElements" })
            }
          />
          {t("smartSurveyor.selectionAreas.appliesToElements")}
        </label>
        <label
          className="flex items-center gap-1.5 whitespace-nowrap text-sm text-gray-600 cursor-pointer"
          onClick={stopRowSelection}
        >
          <ApprovalStatusCheckbox
            checked={area.appliesToRoutes}
            onToggle={() =>
              onToggleFlag({ areaId: area.id, flag: "appliesToRoutes" })
            }
          />
          {t("smartSurveyor.selectionAreas.appliesToCableTrays")}
        </label>
        <Button
          variant="text"
          size="sm"
          onClick={handleDeleteClick}
          title={t("smartSurveyor.selectionAreas.deleteArea")}
          className="!p-0 !ml-auto !text-gray-400 hover:!text-red-600"
        >
          <Trash2 size={18} />
        </Button>
      </div>
    </div>
  );
};
